/**
 * lib/controlAccounts.ts
 * ------------------------------------------------------------------
 * DOES THE LEDGER AGREE WITH THE SUB-LEDGERS?
 *
 * The first thing an accountant does to a system they do not yet trust, and
 * the right instinct. The general ledger says the school is owed P2.6m; the
 * invoice list says the same thing a different way. If those two ever
 * disagree, every report built on either is wrong and nobody can tell which.
 *
 * Each control pairs a GL account with the detail that ought to add up to
 * it. Five are live:
 *
 *   AR_FEES          ↔ the balance of every live invoice
 *   FEES_IN_ADVANCE  ↔ credit balances sitting on pupil accounts
 *   AP_TRADE         ↔ approved payment vouchers not yet paid
 *   PREPAYMENTS      ↔ deferrals not yet released to a month
 *   ACCRUALS         ↔ accruals not yet released
 *
 * The rest — shop stock, petty cash, staff advances — are declared and
 * reported as AWAITING THEIR MODULE. Naming a control the
 * system cannot yet answer is more useful than omitting it: it says the
 * check exists and why it is silent, so nobody assumes the ledger is fully
 * reconciled when half of it is simply unexamined.
 *
 * A CONTROL WITH NO DETAIL IS NOT A CONTROL THAT AGREES. Zero equals zero
 * is not evidence of anything, and reporting it green would mean a bursar
 * scanning a page of ticks learns nothing from them.
 *
 * BEFORE THE CUT-OVER, NOTHING IS WRONG. The posting triggers are inert
 * until the school names the day the books change hands, so the ledger is
 * legitimately empty while the sub-ledgers hold millions. Reported as "not
 * in service" rather than as a P2.6m discrepancy — a red page nobody can
 * act on teaches people to ignore red pages.
 *
 * A DIFFERENCE IS NEVER SMOOTHED OVER. It means a posting path wrote one
 * side and not the other, so it is reported with its sign and, where the
 * shape allows, the records contributing most to it: "P4,000 out" is a
 * shrug, "P4,000 out, and here are the three invoices" is a morning's work.
 *
 * PURE. No Firestore, no React.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';
import type { GlAccount, GlJournal, SystemKey } from '@/lib/gl';

export const CONTROL_TOLERANCE = 0.005;

export type ControlState =
  | 'agrees'
  | 'differs'
  | 'not-in-service'
  | 'awaiting-module'
  | 'no-account';

export interface ControlContributor {
  label: string;
  reference?: string;
  amount: number;
}

export interface ControlCheck {
  key: string;
  label: string;
  systemKey: SystemKey;
  accountCode?: string;
  accountName?: string;
  /** What the ledger says, signed the way the account normally sits. */
  glBalance: number;
  /** What the detail behind it adds up to. */
  subLedger: number;
  /** subLedger − glBalance. */
  difference: number;
  state: ControlState;
  /** What the sub-ledger IS, in a sentence, so a reader can judge the check. */
  detailIs: string;
  /** What to do when it differs. */
  ifDiffers: string;
  contributors: ControlContributor[];
  detailCount: number;
}

export interface ControlReport {
  checks: ControlCheck[];
  agreeing: number;
  differing: number;
  /** Controls that could not be answered — no module, or the ledger is off. */
  unanswered: number;
  allAgree: boolean;
  /** Total absolute difference across every answerable control. */
  totalDifference: number;
  /** True when the ledger has not been brought into service. */
  ledgerNotInService: boolean;
}

/* ---------------------------------------------------------------- */

/**
 * The GL balance for one system key, on the account's normal side.
 *
 * Assets and expenses sit as debits, liabilities/income/equity as credits,
 * so an asset with a debit balance reads positive and so does a liability
 * with a credit balance. A reader comparing to a sub-ledger should not have
 * to hold a sign convention in their head.
 */
function glBalanceFor(
  accounts: GlAccount[],
  journals: GlJournal[],
  systemKey: SystemKey,
): { balance: number; account?: GlAccount } {
  const account = accounts.find((a) => a.systemKey === systemKey);
  if (!account) return { balance: 0 };
  let dr = 0;
  let cr = 0;
  for (const j of journals) {
    if (j.status === 'reversed') continue;
    for (const l of j.lines ?? []) {
      if (l.accountCode !== account.code) continue;
      dr += Number(l.dr) || 0;
      cr += Number(l.cr) || 0;
    }
  }
  const net = account.type === 'asset' || account.type === 'expense' ? dr - cr : cr - dr;
  return { balance: round2(net), account };
}

export interface ControlInvoice {
  id: string;
  invoiceNo?: string;
  studentName?: string;
  status?: string;
  balance?: number;
}

export interface ControlDeferral {
  id: string;
  kind: 'prepayment' | 'accrual';
  description: string;
  active?: boolean;
  /** Still to be charged to a month. */
  outstanding: number;
}

/** An approved, unpaid voucher — what the school owes and has agreed to pay. */
export interface ControlVoucher {
  id: string;
  voucherNo?: string;
  payeeName?: string;
  currentStatus?: string;
  total?: number;
  raisesAccrual?: boolean;
  payableAccountKey?: string;
}

export interface ControlSources {
  accounts: GlAccount[];
  journals: GlJournal[];
  invoices: ControlInvoice[];
  deferrals?: ControlDeferral[];
  vouchers?: ControlVoucher[];
  /** False until the school sets a cut-over date and switches posting on. */
  ledgerInService: boolean;
}

/** The controls this system cannot answer yet, and what each is waiting for. */
const AWAITING: { key: string; label: string; systemKey: SystemKey; detailIs: string }[] = [
  {
    key: 'stock',
    label: 'Stock on hand',
    systemKey: 'STOCK_ON_HAND',
    detailIs: 'the shop stock valuation — awaiting the Uniform Shop module',
  },
  {
    key: 'pettyCash',
    label: 'Petty cash',
    systemKey: 'PETTY_CASH',
    detailIs: 'what the floats say is in the tins — awaiting the Petty Cash module',
  },
  {
    key: 'advances',
    label: 'Staff advances',
    systemKey: 'STAFF_ADVANCES',
    detailIs: 'advances and loans not yet recovered — awaiting the loans sub-ledger',
  },
];

function decide(
  glBalance: number,
  subLedger: number,
  detailCount: number,
  hasAccount: boolean,
  inService: boolean,
): ControlState {
  if (!hasAccount) return 'no-account';
  if (!inService) return 'not-in-service';
  // Zero against zero proves nothing. Say so rather than showing a tick.
  if (detailCount === 0 && glBalance === 0) return 'awaiting-module';
  return Math.abs(round2(subLedger - glBalance)) < CONTROL_TOLERANCE ? 'agrees' : 'differs';
}

export function buildControlReport(src: ControlSources): ControlReport {
  const { accounts, journals, invoices, ledgerInService } = src;
  const deferrals = src.deferrals ?? [];
  const checks: ControlCheck[] = [];

  const add = (
    key: string,
    label: string,
    systemKey: SystemKey,
    subLedger: number,
    detailCount: number,
    detailIs: string,
    ifDiffers: string,
    contributors: ControlContributor[],
  ) => {
    const { balance, account } = glBalanceFor(accounts, journals, systemKey);
    const difference = round2(subLedger - balance);
    checks.push({
      key,
      label,
      systemKey,
      accountCode: account?.code,
      accountName: account?.name,
      glBalance: balance,
      subLedger: round2(subLedger),
      difference,
      state: decide(balance, subLedger, detailCount, !!account, ledgerInService),
      detailIs,
      ifDiffers,
      // Only worth showing when there IS a difference to explain.
      contributors: Math.abs(difference) >= CONTROL_TOLERANCE ? contributors : [],
      detailCount,
    });
  };

  /* ---- debtors ---- */
  const live = invoices.filter((i) => i.status !== 'void');
  const owing = live.filter((i) => (Number(i.balance) || 0) > 0);
  add(
    'debtors',
    'Fees receivable',
    'AR_FEES',
    owing.reduce((a, i) => a + (Number(i.balance) || 0), 0),
    owing.length,
    'the outstanding balance of every invoice that is not void',
    'An invoice was raised, paid or credited without its journal posting. Check the '
      + 'posting log for the dates either side of the difference.',
    [...owing]
      .sort((a, b) => (Number(b.balance) || 0) - (Number(a.balance) || 0))
      .slice(0, 5)
      .map((i) => ({
        label: i.studentName ?? i.id,
        reference: i.invoiceNo,
        amount: round2(Number(i.balance) || 0),
      })),
  );

  /* ---- fees held in advance ----
     A credit balance is money the school is holding, so it is a LIABILITY
     and reads positive against a credit-balance account. Netting it into
     receivables would understate both by the same amount. */
  const inCredit = live.filter((i) => (Number(i.balance) || 0) < 0);
  add(
    'feesInAdvance',
    'Fees held in advance',
    'FEES_IN_ADVANCE',
    inCredit.reduce((a, i) => a + Math.abs(Number(i.balance) || 0), 0),
    inCredit.length,
    'credit balances sitting on pupil accounts — money the school holds, not money it is owed',
    'A payment landed on an invoice without the journal moving it out of receivables.',
    [...inCredit]
      .sort((a, b) => (Number(a.balance) || 0) - (Number(b.balance) || 0))
      .slice(0, 5)
      .map((i) => ({
        label: i.studentName ?? i.id,
        reference: i.invoiceNo,
        amount: round2(Math.abs(Number(i.balance) || 0)),
      })),
  );

  /* ---- trade creditors ----
     Only vouchers that RAISED the payable count. A payroll voucher settles a
     liability the payslip journal already created, so counting it here would
     double the entire payroll against AP_TRADE. */
  const unpaidVouchers = (src.vouchers ?? [])
    .filter((v) => v.currentStatus === 'approved')
    .filter((v) => v.raisesAccrual !== false)
    .filter((v) => (v.payableAccountKey ?? 'AP_TRADE') === 'AP_TRADE');
  add(
    'creditors',
    'Trade creditors',
    'AP_TRADE',
    unpaidVouchers.reduce((a, v) => a + (Number(v.total) || 0), 0),
    unpaidVouchers.length,
    'approved payment vouchers not yet paid',
    'A voucher was approved or paid without its journal posting. Check the posting log '
      + 'around the dates either side of the difference.',
    [...unpaidVouchers]
      .sort((a, b) => (Number(b.total) || 0) - (Number(a.total) || 0))
      .slice(0, 5)
      .map((v) => ({
        label: v.payeeName ?? v.id,
        reference: v.voucherNo,
        amount: round2(Number(v.total) || 0),
      })),
  );

  /* ---- deferrals ---- */
  for (const [kind, key, label, systemKey] of [
    ['prepayment', 'prepayments', 'Prepayments', 'PREPAYMENTS'],
    ['accrual', 'accruals', 'Accruals', 'ACCRUALS'],
  ] as const) {
    const open = deferrals.filter((d) => d.kind === kind && d.active !== false && d.outstanding > 0);
    add(
      key,
      label,
      systemKey,
      open.reduce((a, d) => a + d.outstanding, 0),
      open.length,
      kind === 'prepayment'
        ? 'costs paid but not yet charged to a month'
        : 'costs incurred but not yet billed to the school',
      'A month-end release posted without the schedule being updated, or the other way round.',
      [...open]
        .sort((a, b) => b.outstanding - a.outstanding)
        .slice(0, 5)
        .map((d) => ({ label: d.description, amount: d.outstanding })),
    );
  }

  /* ---- the ones this system cannot answer yet ---- */
  for (const a of AWAITING) {
    const { balance, account } = glBalanceFor(accounts, journals, a.systemKey);
    checks.push({
      key: a.key,
      label: a.label,
      systemKey: a.systemKey,
      accountCode: account?.code,
      accountName: account?.name,
      glBalance: balance,
      subLedger: 0,
      difference: 0,
      state: account ? 'awaiting-module' : 'no-account',
      detailIs: a.detailIs,
      ifDiffers: 'Nothing to do until the module lands.',
      contributors: [],
      detailCount: 0,
    });
  }

  const answerable = checks.filter((c) => c.state === 'agrees' || c.state === 'differs');
  const differing = answerable.filter((c) => c.state === 'differs');

  return {
    checks,
    agreeing: answerable.length - differing.length,
    differing: differing.length,
    unanswered: checks.length - answerable.length,
    allAgree: answerable.length > 0 && differing.length === 0,
    totalDifference: round2(differing.reduce((a, c) => a + Math.abs(c.difference), 0)),
    ledgerNotInService: !ledgerInService,
  };
}

export const CONTROL_STATE_LABEL: Record<ControlState, string> = {
  agrees: 'Agrees',
  differs: 'Does not agree',
  'not-in-service': 'Ledger not in service',
  'awaiting-module': 'Nothing to compare',
  'no-account': 'No account carries this key',
};
